<template>
  <div class="mainfont page-container">
    <v-snackbar v-model="showSnackBar" color="black">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <div v-if="list && (list._id || list.applicationNo)">
      <vue-element-loading :active="appLoading" :is-full-screen="true" background-color="#FFFFFF" color="#13736f"
        spinner="spinner" />

      <PageHeader title="Application details" :subtitle="headerSubtitle" icon="mdi-file-document-outline">
        <template v-slot:actions>
          <v-btn depressed outlined color="#13736f" class="text-none font-weight-bold" height="42" @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon> Back
          </v-btn>
          <v-btn v-if="editTarget" depressed outlined color="#d97706" class="text-none font-weight-bold" height="42" @click="$router.push(editTarget)">
            <v-icon left>mdi-pencil-outline</v-icon> {{ isDraft ? "Edit" : "Edit & resubmit" }}
          </v-btn>
          <v-btn v-if="!isDraft" depressed outlined color="#2563eb" class="text-none font-weight-bold" height="42" @click="$router.push('/recordspage?id=' + list._id)">
            <v-icon left>mdi-timeline-clock-outline</v-icon> View timeline
          </v-btn>
        </template>
      </PageHeader>

      <v-row>
        <!-- Main column -->
        <v-col cols="12" md="8" class="py-0">
          <!-- 1. Nature Camp & Schedule Details -->
          <section class="surface detail-section mb-5">
            <div class="detail-section-head">
              <h3 class="section-title mb-0"><v-icon>mdi-tent</v-icon>Nature camp &amp; schedule</h3>
              <span v-if="list.district" class="status-pill status-pill--neutral">{{ list.district }}</span>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">District name</div>
                <div class="info-value">{{ list.district || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">From</div>
                <div class="info-value">{{ formatDate(list.startDate) || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">To</div>
                <div class="info-value">{{ formatDate(list.endDate) || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Duration of camp</div>
                <div class="info-value">{{ list.campDuration ? list.campDuration + ' Day(s)' : 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="list.campid && list.campid.campName">
                <div class="info-label">Camp center</div>
                <div class="info-value">{{ list.campid.campName }}</div>
              </div>
            </div>
          </section>

          <!-- 2. Applicant & Participant Summary -->
          <section class="surface detail-section mb-5">
            <div class="detail-section-head">
              <h3 class="section-title mb-0"><v-icon>mdi-account-group-outline</v-icon>Applicant &amp; participants</h3>
              <span v-if="list.applicantCategory" class="status-pill status-pill--info category-pill">{{ list.applicantCategory }}</span>
            </div>
            <div class="count-grid">
              <div class="count-tile count-tile--total">
                <v-icon color="#13736f">mdi-account-multiple-outline</v-icon>
                <div>
                  <div class="count-value">{{ list.memberCount || 0 }}</div>
                  <div class="count-label">Total participants</div>
                </div>
              </div>
              <div class="count-tile">
                <v-icon color="#2563eb">mdi-gender-male</v-icon>
                <div>
                  <div class="count-value">{{ list.maleCount || 0 }}</div>
                  <div class="count-label">No. of males</div>
                </div>
              </div>
              <div class="count-tile">
                <v-icon color="#db2777">mdi-gender-female</v-icon>
                <div>
                  <div class="count-value">{{ list.femaleCount || 0 }}</div>
                  <div class="count-label">No. of females</div>
                </div>
              </div>
              <div class="count-tile">
                <v-icon color="#7c3aed">mdi-gender-transgender</v-icon>
                <div>
                  <div class="count-value">{{ list.transCount || 0 }}</div>
                  <div class="count-label">No. of transgenders</div>
                </div>
              </div>
            </div>
            <div class="info-grid mt-3">
              <div class="info-item">
                <div class="info-label">Applicant category</div>
                <div class="info-value">{{ list.applicantCategory || 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="list.applicantCategory === 'Students'">
                <div class="info-label">Student category</div>
                <div class="info-value">{{ list.studentCategory || 'N/A' }}</div>
              </div>
            </div>
          </section>


          <!-- 4. Head of Institution Details (if students) -->
          <section class="surface detail-section mb-5" v-if="list && list.applicantCategory === 'Students'">
            <h3 class="section-title"><v-icon>mdi-account-tie-outline</v-icon>Head of institution</h3>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Name of head</div>
                <div class="info-value">{{ list.instHeadName || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Designation</div>
                <div class="info-value">{{ list.headDesignation || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">ID card type</div>
                <div class="info-value">{{ list.idCardType || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">ID card number</div>
                <div class="info-value">{{ list.idNo || 'N/A' }}</div>
              </div>
            </div>
          </section>
        </v-col>

        <!-- Side column -->
        <v-col cols="12" md="4" class="py-0">
         <div>
          <!-- Application status -->
          <section class="surface detail-section mb-5">
            <h3 class="section-title"><v-icon>mdi-clipboard-text-outline</v-icon>Application</h3>
            <div class="side-list">
              <div class="side-row">
                <span class="side-label">Application no.</span>
                <span class="app-no">{{ list.applicationNo || list.applicationId || list._id }}</span>
              </div>
              <div class="side-row" v-if="list.created_at">
                <span class="side-label">Applied on</span>
                <span class="side-value">{{ formatDate(list.created_at) }}</span>
              </div>
              <div class="side-row" v-if="list.status">
                <span class="side-label">Status</span>
                <span class="status-pill" :class="pillClass(list.status)">{{ list.status }}</span>
              </div>
              <div class="side-row" v-if="paymentInfo">
                <span class="side-label">Payment</span>
                <span class="status-pill" :class="'status-pill--' + paymentInfo.tone">{{ paymentInfo.label }}</span>
              </div>
              <div class="side-row" v-if="list.amount">
                <span class="side-label">Amount</span>
                <span class="side-value side-value--amount">&#8377; {{ list.amount }}/-</span>
              </div>
            </div>
          </section>

          <!-- 7. Uploaded Documents -->
          <section class="surface detail-section mb-5">
            <h3 class="section-title"><v-icon>mdi-file-document-multiple-outline</v-icon>Uploaded documents</h3>
            <div class="doc-list">
              <!-- ID Proof Head of Institution -->
              <a v-if="list && list.applicantCategory === 'Students' && idProof" class="doc-item" :href="mediaURL + idProof" target="_blank" download>
                <v-icon color="#dc2626">mdi-file-pdf-box</v-icon>
                <span class="doc-name">ID proof of head of institution</span>
                <v-icon small color="#13736f">mdi-download-outline</v-icon>
              </a>

              <!-- Request Letter -->
              <a v-if="reqLetter" class="doc-item" :href="mediaURL + reqLetter" target="_blank" download>
                <v-icon color="#dc2626">mdi-file-pdf-box</v-icon>
                <span class="doc-name">Request letter from head</span>
                <v-icon small color="#13736f">mdi-download-outline</v-icon>
              </a>

              <!-- Participants List -->
              <a v-if="participantfile" class="doc-item" :href="mediaURL + participantfile" target="_blank" download>
                <v-icon color="#dc2626">mdi-file-pdf-box</v-icon>
                <span class="doc-name">List of participants (signed)</span>
                <v-icon small color="#13736f">mdi-download-outline</v-icon>
              </a>

              <!-- Leader ID Proof -->
              <a v-if="leaderIdProof" class="doc-item" :href="mediaURL + leaderIdProof" target="_blank" download>
                <v-icon color="#dc2626">mdi-file-pdf-box</v-icon>
                <span class="doc-name">ID proof of group leader / staff</span>
                <v-icon small color="#13736f">mdi-download-outline</v-icon>
              </a>

              <div v-if="!hasDocuments" class="empty-state py-6">
                <v-icon large>mdi-file-hidden</v-icon>
                <span>No documents uploaded</span>
              </div>
            </div>
          </section>

          <!-- 3. Institution Details -->
          <section class="surface detail-section mb-5">
            <h3 class="section-title"><v-icon>mdi-domain</v-icon>Institution / organisation</h3>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Name of institution</div>
                <div class="info-value">{{ list.instName || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Institution code</div>
                <div class="info-value">{{ list.instCode || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Type of institution</div>
                <div class="info-value">{{ list.instType || 'N/A' }}</div>
              </div>
              <div class="info-item info-item--wide">
                <div class="info-label">Address</div>
                <div class="info-value">{{ list.instAddress || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">District</div>
                <div class="info-value">{{ list.instDistrict || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Telephone number</div>
                <div class="info-value">{{ list.instTeleNo || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Mobile number</div>
                <div class="info-value">{{ list.instMobileNo || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Email address</div>
                <div class="info-value">{{ list.instEmail || 'N/A' }}</div>
              </div>
            </div>
          </section>
         </div>
        </v-col>
      </v-row>

      <!-- 5. Group Leader / Staff Details -->
      <section class="surface mb-5" v-if="leaderDetails && leaderDetails.length > 0">
        <div class="surface-toolbar">
          <div>
            <div class="surface-title">Group leader / teaching staff</div>
            <div class="surface-subtitle">{{ leaderDetails.length }} Leaders</div>
          </div>
        </div>
        <div class="table-scroll">
          <v-simple-table class="app-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Name</th>
                  <th class="text-left">Designation / address</th>
                  <th class="text-left">Contact no</th>
                  <th class="text-left">Email</th>
                  <th class="text-center">Gender</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in leaderDetails" :key="i">
                  <td data-label="Name" class="cell-strong">{{ item.leaderName || '—' }}</td>
                  <td data-label="Designation / address">
                    <div>
                      <div class="font-weight-medium">{{ item.leaderAddress1 || '—' }}</div>
                      <div class="cell-muted" v-if="item.leaderAddress2">{{ item.leaderAddress2 }}</div>
                    </div>
                  </td>
                  <td data-label="Contact no">
                    <span class="text-no-wrap"><v-icon x-small color="#64748b" class="mr-1">mdi-phone-outline</v-icon>{{ item.leaderPhone || item.telephone || '—' }}</span>
                  </td>
                  <td data-label="Email">
                    <span class="cell-break">{{ item.leaderEmail || '—' }}</span>
                  </td>
                  <td data-label="Gender" class="text-center">
                    <span class="status-pill" :class="item.leaderGender === 'Female' ? 'gender-pill--female' : 'status-pill--info'">
                      {{ item.leaderGender || '—' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </section>

      <!-- 6. Member Details -->
      <section class="surface mb-5" v-if="memberDetails && memberDetails.length > 0">
        <div class="surface-toolbar">
          <div>
            <div class="surface-title">Participant / member list</div>
            <div class="surface-subtitle">{{ memberDetails.length }} Members</div>
          </div>
        </div>
        <div class="table-scroll">
          <v-simple-table class="app-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" style="width: 70px">Partcipant No.</th>
                  <th class="text-left">Name</th>
                  <th class="text-left">Date of birth</th>
                  <th class="text-center">Gender</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in memberDetails" :key="i">
                  <td data-label="#" class="text-center cell-index">{{ i + 1 }}</td>
                  <td data-label="Member name" class="cell-strong">{{ item.memberName || '—' }}</td>
                  <td data-label="Date of birth">{{ item.memberDob || '—' }}</td>
                  <td data-label="Gender" class="text-center">
                    <span class="status-pill" :class="item.memberGender === 'Female' ? 'gender-pill--female' : 'status-pill--info'">
                      {{ item.memberGender || '—' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </section>

      <!-- Pending actions -->
      <div class="sticky-actions action-bar" v-if="showActionBar">
        <v-btn depressed outlined color="#d97706" class="text-none font-weight-bold" height="42"
          v-if="editTarget && isDraft" @click="$router.push(editTarget)">
          <v-icon left>mdi-pencil-outline</v-icon> Edit
        </v-btn>

        <v-btn depressed color="#13736f" class="white--text text-none font-weight-bold" height="42"
          v-if="list.paymentStatus === 'Pending' && list.status !== 'Cancelled' && list.divisonApprovalStatus !== 'Success' && list.finalSubmit !== 'true'"
          @click="submit()">
          <v-icon left>mdi-check-circle-outline</v-icon> Submit application
        </v-btn>

        <v-btn depressed color="#13736f" class="white--text text-none font-weight-bold" height="42"
          v-if="list.paymentStatus === 'Pending' && list.status !== 'Cancelled' && list.divisonApprovalStatus === 'Success' && list.superAdminApprovalStatus === 'Success' && list.signedsanction && list.campid && list.campid.campType !== 'free'"
          @click="rePaymentDialog = true">
          <v-icon left>mdi-credit-card-outline</v-icon> Proceed to payment
        </v-btn>
      </div>
    </div>


    <v-dialog :retain-focus="true" persistent v-model="rePaymentDialog" max-width="800px" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="d-flex flex-column fill-height">
        <div class="dialog-head">
          <div>
            <div class="dialog-head-title mainfont">Payment details</div>
            <div class="dialog-head-subtitle">Please enter the details below and proceed for payment.</div>
          </div>
          <v-btn icon dark @click="paymentdialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-5 flex-grow-1">
          <div class="amount-banner mb-5">
            <v-icon color="#b45309">mdi-currency-inr</v-icon>
            <span>Amount to be paid: <strong>{{ list.amount }}/-</strong></span>
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field type="text" dense clearable outlined class="inputPrice" v-model="paymentname"
                prepend-inner-icon="mdi-account-outline"
                :rules="[(v) => !!v || 'Name is required']">
                <template v-slot:label>
                  <span class="custom-label-text">
                    Name
                    <span class="req-star">*</span>
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field type="text" dense clearable outlined class="inputPrice" v-model="paymentaddress"
                prepend-inner-icon="mdi-map-marker-outline"
                :rules="[(v) => !!v || 'Address is required']">
                <template v-slot:label>
                  <span class="custom-label-text">
                    Address
                    <span class="req-star">*</span>
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field type="number" dense clearable outlined class="inputPrice" v-model="paymentphonenumber"
                prepend-inner-icon="mdi-phone-outline"
                :rules="[
                  (v) => !!v || 'Phone number is required',
                  (v) =>
                    /^[0-9]{10}$/.test(v) ||
                    'Phone number must have 10 digits',
                ]">
                <template v-slot:label>
                  <span class="custom-label-text">
                    Phone Number
                    <span class="req-star">*</span>
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field type="number" dense clearable outlined class="inputPrice" v-model="paymentpincode"
                prepend-inner-icon="mdi-mailbox-outline"
                :rules="[
                  (v) => !!v || 'Pincode is required',
                  (v) =>
                    /^[0-9]{6}$/.test(v) || 'Pincode must have 6 digits',
                ]">
                <template v-slot:label>
                  <span class="custom-label-text">
                    Pincode
                    <span class="req-star">*</span>
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field type="text" dense clearable outlined class="inputPrice" v-model="paymentpancardnumber"
                prepend-inner-icon="mdi-card-account-details-outline"
                :rules="[
                  (v) => !!v || 'PAN Card Number is required',
                  (v) =>
                    /^[A-Za-z0-9]{10}$/.test(v) ||
                    'PAN Card Number must have 10 characters',
                ]">
                <template v-slot:label>
                  <span class="custom-label-text">
                    PAN Card Number
                    <span class="req-star">*</span>
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <!-- <v-flex xs12 lg6 pl-2>
                          <v-text-field
                            type="number"
                            dense
                            clearable
                            outlined
                            class="inputPrice"
                            v-model="paymenttaxid"
                            :rules="[(v) => !!v || 'TAX Id is required']"
                          >
                            <template v-slot:label>
                              <span
                                class="custom-label-text"
                                style="color: black; font-size: 14px"
                              >
                                TAX Id
                                <span style="color: red; font-size: 20px"
                                  >*</span
                                >
                              </span>
                            </template>
                          </v-text-field>
                        </v-flex> -->
          </v-row>
        </v-card-text>
        <v-card-actions class="pb-5 px-6">
          <v-spacer></v-spacer>
          <v-btn text @click="paymentdialog()" class="mainfont text-none font-weight-bold">Cancel</v-btn>
          <v-btn depressed color="#13736f" class="mainfont white--text text-none font-weight-bold px-6" :disabled="submitted" @click="Paymentsubmit()">
            {{ submitted ? "Processing..." : "Proceed For Payment" }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import PageHeader from "@/components/Common/PageHeader";
import { paymentBadge } from "@/utils/applicationHelper";
import { getUserType } from "@/components/Common/navConfig";
// import CryptoJS from "crypto-js";
export default {
  components: {
    PageHeader,
  },
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      memberdetails: [],
      memberDetails: [],
      leaderName: null,
      leaderAddress1: null,
      leaderAddress2: null,
      telephone: null,
      idCardType: null,
      leaderPhone: null,
      leaderGender: null,
      leaderEmail: null,
      district: null,
      instHeadName: null,
      headDesignation: null,
      idCardTypeidCardType: null,
      idNo: null,
      startDate: today,
      showSnackBar: false,
      isChecked: false,
      appLoading: false,
      msg: "",
      expandSection: false,
      name: null,
      barlist: [],
      list: {},
      selectedDistrict: null,
      campDuration: "",
      getdetailsdata: {},
      menu1: false,
      male: null,
      memberCount: null,
      leaderdetails: [],
      idProof: "",
      reqLetter: "",
      participantfile: "",
      leaderIdProof: "",
      leaderDetails: [],
      applicantCategory: "",
      studentCategory: "",
      studentCategories: [
        "Upper Primary",
        "High School",
        "Higher Secondary",
        "College/University",
      ],
      applicantname: null,
      applicantcategorys: [
        "Students",
        "Press Reporters",
        "LSG Members",
        "NGO youth/charitable organization",
      ],
      amountBreakdownSummary: null,
      amount: null,
      conductedDialog: false,
      notConductedDialog: false,
      feedback: {
        conductDetails: '',
        conductTime: '',
        participantRemarks: '',
        notConductedReason: '',
        notConductedRemarks: ''
      },
      id: this.$route.query.id,

      rePaymentDialog: false,
      submitted: false,
      paymentphonenumber: "",
      paymentname: "",
      paymentpancardnumber: "",
      paymentaddress: "",
      paymentpincode: "",
      paymenttaxid: "",
      totalAmount: "",
      appId: "",
    };
  },
  mounted() {
    this.getList();
  },
  computed: {
    // Payment is due only after approval; before that it reads "Due after approval".
    paymentInfo() {
      return paymentBadge(this.list);
    },
    calculatedTotalAmount() {
      const totalMembers = this.memberDetails.length;
      return this.totalAmount * totalMembers * this.list.campDuration;
    },
    // A draft has not been submitted yet: it has no history to show and can be edited freely.
    isDraft() {
      return !!this.list && this.list.finalSubmit !== "true";
    },
    // Where the applicant can edit this application, or null (staff, cancelled, locked).
    editTarget() {
      const l = this.list || {};
      if (getUserType() !== "user" || !l._id || l.status === "Cancelled") return null;
      if (this.isDraft) return { path: "/editbooking", query: { id: l._id } };
      if (l.editStatus === "Able") {
        return {
          path: "/EditForwardApplication",
          query: { id: l._id, district: (l.campid && l.campid.district) || "" },
        };
      }
      return null;
    },
    headerSubtitle() {
      const appNo = this.list.applicationNo || this.list.applicationId || this.list._id;
      return appNo ? "Application No. " + appNo : "";
    },
    hasDocuments() {
      return !!(
        (this.list && this.list.applicantCategory === "Students" && this.idProof) ||
        this.reqLetter ||
        this.participantfile ||
        this.leaderIdProof
      );
    },
    showActionBar() {
      const l = this.list || {};
      const canSubmit =
        l.paymentStatus === "Pending" &&
        l.status !== "Cancelled" &&
        l.divisonApprovalStatus !== "Success" &&
        l.finalSubmit !== "true";
      const canPay =
        l.paymentStatus === "Pending" &&
        l.status !== "Cancelled" &&
        l.divisonApprovalStatus === "Success" &&
        l.superAdminApprovalStatus === "Success" &&
        l.signedsanction &&
        l.campid &&
        l.campid.campType !== "free";
      return !!(canSubmit || canPay);
    },
  },
  methods: {
    pillClass(value) {
      const v = String(value || "").toLowerCase();
      if (v.includes("cancel") || v.includes("reject") || v.includes("fail")) return "status-pill--danger";
      if (v.includes("success") || v.includes("approve") || v.includes("paid") || v.includes("complete")) return "status-pill--success";
      if (v.includes("pending") || v.includes("process") || v.includes("forward")) return "status-pill--warning";
      return "status-pill--neutral";
    },
    submitFeedback(status) {
      let payload = {
        applicationId: this.list._id,
        isConducted: status,
        ...this.feedback
      };
      
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/update/conduct-status",
        headers: {
          token: localStorage.getItem("token")
        },
        data: payload
      }).then((res) => {
        this.appLoading = false;
        if (res.data.status) {
          this.msg = res.data.msg;
          this.showSnackBar = true;
          this.conductedDialog = false;
          this.notConductedDialog = false;
          this.getList();
        } else {
          this.msg = res.data.msg || "Failed to submit feedback";
          this.showSnackBar = true;
        }
      }).catch(() => {
        this.appLoading = false;
        this.msg = "Internal server error";
        this.showSnackBar = true;
      });
    },
    goBack() {
      // Use the router to navigate back to the previous page
      this.$router.go(-1);
    },
    formatDate(item) {
      var dt = new Date(item);
      var year = dt.getFullYear();
      dt = dt.toString();
      var strTime = dt.slice(4, 10) + " " + year;
      return strTime;
    },
    getList() {
      this.appLoading = true;
      // const encryptedId = this.$route.query.id;
      // const decryptedId = CryptoJS.AES.decrypt(encryptedId, 'encryptionSecret1234)(*&^%$#:">?")qws').toString(CryptoJS.enc.Utf8);
      axios({
        method: "POST",
        url: "/single/application",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          // appid:  decryptedId,
          appid: this.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.list = response.data.data;
            this.leaderDetails = this.list.leaderDetails;
            this.memberDetails = this.list.memberDetails;
            this.idProof = this.list.idProof;
            this.reqLetter = this.list.reqLetter;
            this.participantfile = this.list.participantfile;
            this.leaderIdProof = this.list.leaderIdProof;
            this.appId = this.list._id;
            if (this.list && this.list.campid) {
              this.totalAmount = this.list.campid.rate;
              console.log("This Rate:", this.list.campid.rate);
            }
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },

    paymentdialog() {
      this.rePaymentDialog = false;
      this.submitted = false;
      this.paymentphonenumber = "";
      this.paymentname = "";
      this.paymentpancardnumber = "";
      this.paymentaddress = "";
      this.paymentpincode = "";
      this.paymenttaxid = "";
    },

    openFormInNewWindow() {
      // Create a form element
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        "https://etreasury.kerala.gov.in/api/eTreasury/service/ChallanGeneration.php"; // Replace with your actual endpoint URL
      form.target = "_blank"; // Open in a new window or tab

      // Create hidden input fields for 'input_data' and 'input_headers'
      const inputDataField = document.createElement("input");
      inputDataField.type = "hidden";
      inputDataField.name = "input_data";
      inputDataField.value = JSON.stringify({
        HMAC: this.getdetailsdata.HMAC, // Replace with actual HMAC value

        DATA: this.getdetailsdata.DATA,

        // Replace with actual encrypted data
      });

      const inputHeadersField = document.createElement("input");
      inputHeadersField.type = "hidden";
      inputHeadersField.name = "input_headers";
      inputHeadersField.value = JSON.stringify({
        clientId: this.getdetailsdata.clientId,
        authToken: this.getdetailsdata.authToken,
        // Replace with actual auth token
      });

      // Append hidden inputs to the form
      form.appendChild(inputDataField);
      form.appendChild(inputHeadersField);

      // Append form to the body (required to submit it)
      document.body.appendChild(form);

      // Submit the form
      form.submit();

      // Remove the form from the DOM after submission
      document.body.removeChild(form);
    },
    submit() {
      const formData = new FormData();
      formData.append("appid", this.appId);
      // This page is read-only: the saved application is submitted as it is, and the
      // server checks that it is complete (unset fields used to go up as "undefined").
      axios({
        method: "POST",
        url: "/final/submit",
        headers: {
          token: localStorage.getItem("token"),
          "Content-Type": "multipart/form-data"
        },
        data: formData,
      })
        .then((response) => {
          if (response.data.status == true) {
            this.submitdialog = false;
            this.$appAlert({
              type: "success",
              title: "Application submitted!",
              highlight: (String(response.data.msg || "").match(/\(([^)]+)\)/) || [])[1],
              message: response.data.msg,
            });
            this.$router.push({ path: "/SubmittedApplications" });
          } else {
            this.submitdialog = false;
            const errors = response.data.errors;
            if (errors && errors.length && this.$appAlert) {
              // The dialog lists every problem, so skip the snackbar that repeats the first one.
              const shown = errors.slice(0, 8).join(" ");
              const more = errors.length > 8 ? ` (+${errors.length - 8} more)` : "";
              this.$appAlert({
                type: "warning",
                title: "Complete the application first",
                message: shown + more + " Use Edit to fill in the missing details.",
              });
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },
    Paymentsubmit() {
      if (!this.paymentname) {
        this.msg = "Provide Name For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentaddress) {
        this.msg = "Provide Address For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentphonenumber) {
        this.msg = "Provide Phone Number For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentpincode) {
        this.msg = "Provide Pincode For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentpancardnumber) {
        this.msg = "Provide PAN Card Number For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[0-9]{10}$/.test(this.paymentphonenumber)) {
        this.msg = "Phone Number must be 10 digits.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[0-9]{6}$/.test(this.paymentpincode)) {
        this.msg = "Pincode must be 6 digits.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[A-Za-z0-9]{10}$/.test(this.paymentpancardnumber)) {
        this.msg = "PAN Card Number must have exactly 10 characters.";
        this.showSnackBar = true;
        return;
      }
      this.submitted = true;
      // const formData = new FormData();
      // formData.append("appid", this.appId);
      // formData.append("applicantname", this.applicantname);
      // formData.append("amount", this.list.amount);
      // formData.append("address", this.paymentaddress);
      // formData.append("mobnumber", this.paymentphonenumber);
      // formData.append("pin_no", this.paymentpincode);
      // formData.append("pan_no", this.paymentpancardnumber);
      // formData.append("tax_id", this.paymenttaxid);
      // formData.append("party_name", this.paymentname);

      // formData.append("idProof", this.list.idProof);
      // formData.append("reqLetter", this.list.reqLetter);
      // formData.append("participantfile", this.list.participantfile);
      // formData.append("leaderIdProof", this.list.leaderIdProof);
      // formData.append("leaderDetails", JSON.stringify(this.list.leaderDetails));
      // formData.append("memberDetails", JSON.stringify(this.list.memberDetails));
      // formData.append("instHeadName", this.list.instHeadName);
      // formData.append("headDesignation", this.list.headDesignation);
      // formData.append("idCardType", this.list.idCardType);
      // formData.append("idNo", this.list.idNo);
      // formData.append("instName", this.list.instName);
      // formData.append("instCode", this.list.instCode);
      // formData.append("instType", this.list.instType);
      // formData.append("instDistrict", this.list.instDistrict);
      // formData.append("instAddress", this.list.instAddress);
      // formData.append("instTeleNo", this.list.instTeleNo);
      // formData.append("instMobileNo", this.list.instMobileNo);
      // formData.append("instEmail", this.list.instEmail);
      // formData.append("startDate", this.list.startDate);
      // formData.append("district", this.list.district);
      // formData.append("campDuration", this.list.campDuration);
      // formData.append("applicantCategory", this.list.applicantCategory);
      // formData.append("studentCategory", this.list.studentCategory);
      axios({
        method: "POST",
        url: "/initiate/payment",
        data: {
          appid: this.appId,
          amount: this.list.amount,
          address: this.paymentaddress,
          mobnumber: this.paymentphonenumber,
          pin_no: this.paymentpincode,
          pan_no: this.paymentpancardnumber,
          party_name: this.paymentname,
          // isNamudekeralam: true,
        },
        headers: {
          // "Content-Type": "multipart/form-data",
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.submitdialog = false;
            // alert(response.data.msg);
            this.getdetailsdata = response.data.data;
            this.openFormInNewWindow();
            this.$router.push({ path: "/mybooking" });
            // window.location.reload();
            // this.$router.push({ path: "/NatureCamps" });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.submitted = false;
          }
        })
        .catch((error) => {
          // Handle 400 status code and other errors
          if (error.response && error.response.status === 400) {
            this.msg = error.response.data.msg || "Bad request error occurred";
          } else {
            this.msg = "An error occurred while processing your request";
          }
          this.showSnackBar = true;
          this.submitted = false;
          console.error(error);
        });
    },
    // Test Payment Submit
    TestPaymentsubmit() {
      if (!this.paymentname) {
        this.msg = "Provide Name For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentaddress) {
        this.msg = "Provide Address For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentphonenumber) {
        this.msg = "Provide Phone Number For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentpincode) {
        this.msg = "Provide Pincode For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!this.paymentpancardnumber) {
        this.msg = "Provide PAN Card Number For Payment Procedure.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[0-9]{10}$/.test(this.paymentphonenumber)) {
        this.msg = "Phone Number must be 10 digits.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[0-9]{6}$/.test(this.paymentpincode)) {
        this.msg = "Pincode must be 6 digits.";
        this.showSnackBar = true;
        return;
      }
      if (!/^[A-Za-z0-9]{10}$/.test(this.paymentpancardnumber)) {
        this.msg = "PAN Card Number must have exactly 10 characters.";
        this.showSnackBar = true;
        return;
      }
      this.submitted = true;
      // const formData = new FormData();
      // formData.append("appid", this.appId);
      // formData.append("applicantname", this.applicantname);
      // formData.append("amount", this.list.amount);
      // formData.append("address", this.paymentaddress);
      // formData.append("mobnumber", this.paymentphonenumber);
      // formData.append("pin_no", this.paymentpincode);
      // formData.append("pan_no", this.paymentpancardnumber);
      // formData.append("tax_id", this.paymenttaxid);
      // formData.append("party_name", this.paymentname);

      // formData.append("idProof", this.list.idProof);
      // formData.append("reqLetter", this.list.reqLetter);
      // formData.append("participantfile", this.list.participantfile);
      // formData.append("leaderIdProof", this.list.leaderIdProof);
      // formData.append("leaderDetails", JSON.stringify(this.list.leaderDetails));
      // formData.append("memberDetails", JSON.stringify(this.list.memberDetails));
      // formData.append("instHeadName", this.list.instHeadName);
      // formData.append("headDesignation", this.list.headDesignation);
      // formData.append("idCardType", this.list.idCardType);
      // formData.append("idNo", this.list.idNo);
      // formData.append("instName", this.list.instName);
      // formData.append("instCode", this.list.instCode);
      // formData.append("instType", this.list.instType);
      // formData.append("instDistrict", this.list.instDistrict);
      // formData.append("instAddress", this.list.instAddress);
      // formData.append("instTeleNo", this.list.instTeleNo);
      // formData.append("instMobileNo", this.list.instMobileNo);
      // formData.append("instEmail", this.list.instEmail);
      // formData.append("startDate", this.list.startDate);
      // formData.append("district", this.list.district);
      // formData.append("campDuration", this.list.campDuration);
      // formData.append("applicantCategory", this.list.applicantCategory);
      // formData.append("studentCategory", this.list.studentCategory);
      axios({
        method: "POST",
        url: "/test/initiate/payment",
        data: {
          appid: this.appId,
          amount: this.list.amount,
          address: this.paymentaddress,
          mobnumber: this.paymentphonenumber,
          pin_no: this.paymentpincode,
          pan_no: this.paymentpancardnumber,
          party_name: this.paymentname,
        },
        headers: {
          // "Content-Type": "multipart/form-data",
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.submitdialog = false;
            // alert(response.data.msg);
            this.getdetailsdata = response.data.data;
            this.openFormInNewWindow();
            this.$router.push({ path: "/mybooking" });
            // window.location.reload();
            // this.$router.push({ path: "/NatureCamps" });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.submitted = false;
          }
        })
        .catch((error) => {
          // Handle 400 status code and other errors
          if (error.response && error.response.status === 400) {
            this.msg = error.response.data.msg || "Bad request error occurred";
          } else {
            this.msg = "An error occurred while processing your request";
          }
          this.showSnackBar = true;
          this.submitted = false;
          console.error(error);
        });
    },
  },
};
</script>


<style scoped>
.detail-section {
  padding: 20px;
}

.detail-section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.category-pill {
  white-space: normal;
  max-width: 100%;
}

.info-item--wide {
  grid-column: 1 / -1;
}

/* Tiles wrap and stretch, so a short last row fills the width instead of leaving blanks */
.info-grid {
  display: flex;
  flex-wrap: wrap;
}

.info-grid > .info-item {
  flex: 1 1 200px;
}

.info-grid > .info-item--wide {
  flex-basis: 100%;
}

.count-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.count-grid > .count-tile {
  flex: 1 1 170px;
}

.count-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafb;
  border: 1px solid #eef2f4;
  min-width: 0;
}

.count-tile--total {
  background: rgba(19, 115, 111, 0.08);
  border-color: rgba(19, 115, 111, 0.2);
}

.count-value {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.1;
  color: #0f172a;
}

.count-tile--total .count-value {
  color: #13736f;
}

.count-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.side-list {
  display: flex;
  flex-direction: column;
}

.side-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px dashed #edf1f3;
}

.side-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.side-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
  flex-shrink: 0;
}

.side-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-align: right;
}

.side-value--amount {
  color: #13736f;
  font-size: 16px;
}

.app-no {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 8px;
  background: rgba(19, 115, 111, 0.1);
  color: #0c4f4c;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.3px;
  word-break: break-all;
  text-align: right;
}

.doc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.doc-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e6edf0;
  background: #f8fafb;
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.doc-item:hover {
  border-color: rgba(19, 115, 111, 0.4);
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.doc-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.cell-strong {
  font-weight: 700;
  color: #0f172a;
}

.cell-muted {
  font-size: 12px;
  color: #64748b;
}

.cell-index {
  font-weight: 700;
  color: #94a3b8;
}

.cell-break {
  word-break: break-all;
  font-size: 13px;
}

.gender-pill--female {
  background: #fce7f3;
  color: #be185d;
}

.action-bar {
  margin-top: 4px;
  border: 1px solid #e6edf0;
  border-radius: 14px;
}

.amount-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 15px;
}

.custom-label-text {
  color: #334155;
  font-size: 14px;
}

.req-star {
  color: #dc2626;
  font-weight: 700;
}

.inputPrice >>> input::-webkit-outer-spin-button,
.inputPrice >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

@media (max-width: 960px) {
  .count-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .detail-section {
    padding: 14px;
  }

  .count-grid {
    gap: 8px;
  }

  .count-tile {
    padding: 10px 12px;
    gap: 8px;
  }

  .info-item--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .info-grid > .info-item {
    flex-basis: 140px;
  }

  .count-grid > .count-tile {
    flex-basis: 130px;
  }
}
</style>
